import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { colors, radii, screenPadding, shadows, spacing } from '../../theme';
import Icon from '../ui/Icon';
import RotatingHint from '../ui/RotatingHint';

/**
 * Search entry point. "Search for" is fixed; the term after it cycles through
 * the catalogue so the field advertises what can be found.
 *
 * The rotator is presentation only - this is a button that opens search, not a
 * live input, so the hint never has to survive being typed over. The search
 * screen shows the same one over its real field, which is why the animation
 * lives in `RotatingHint` rather than here.
 */

type Props = {
  hints: string[];
  onPress?: () => void;
};

function SearchBar({ hints, onPress }: Props) {
  return (
    <View style={styles.row}>
      <Pressable
        accessibilityRole="search"
        accessibilityLabel="Search for a service"
        onPress={onPress}
        style={({ pressed }) => [styles.field, pressed ? styles.pressed : null]}
      >
        <Icon name="search" size={19} color={colors.inkMuted} />
        <RotatingHint hints={hints} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.sm,
  },
  field: {
    flex: 1,
    // A real touch target, not just clear of the 44pt floor - 38 read as a
    // hairline strip next to everything else on the hero.
    height: 48,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: radii.xl,
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  pressed: {
    opacity: 0.7,
  },
});

/* Memoised: the rotating hint inside it animates on its own timer, and it has
   no reason to redraw because the page around it did. */
export default React.memo(SearchBar);
